// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useEffect, useState } from "react";
import { getRouteByPath, type AppRoute } from "../types/routing";
import { pushHistoryEntry, replaceHistoryEntry } from "./historyIndex";

function getCurrentPathname(): string {
  return window.location.pathname || "/";
}

export interface NavigateOptions {
  replace?: boolean;
}

export function navigateTo(path: string, options: NavigateOptions = {}): void {
  if (window.location.pathname === path) {
    return;
  }

  if (options.replace) {
    replaceHistoryEntry(path);
  } else {
    pushHistoryEntry(path);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function currentRouteUrl(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export function withReturnTo(target: string): string {
  const source = currentRouteUrl();
  const sep = target.includes("?") ? "&" : "?";
  return `${target}${sep}returnTo=${encodeURIComponent(source)}`;
}

const ENCODED_PATH_SEPARATOR = /%(2f|5c)/i;
const SINGLE_LEADING_SLASH = /^\/(?![/\\])/;

function hasUnsafeCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (character === "\\" || code < 0x20 || code === 0x7f) {
      return true;
    }
  }
  return false;
}

export function readSafeReturnTo(search: string = window.location.search): string | undefined {
  const value = new URLSearchParams(search).get("returnTo");
  if (
    value === null ||
    !SINGLE_LEADING_SLASH.test(value) ||
    hasUnsafeCharacter(value)
  ) {
    return undefined;
  }
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return undefined;
  }
  if (url.origin !== window.location.origin || ENCODED_PATH_SEPARATOR.test(url.pathname)) {
    return undefined;
  }
  const normalized = `${url.pathname}${url.search}${url.hash}`;
  return SINGLE_LEADING_SLASH.test(normalized) ? normalized : undefined;
}

export function useCurrentRoute(): AppRoute {
  const [route, setRoute] = useState<AppRoute>(() =>
    getRouteByPath(getCurrentPathname()),
  );

  const updateRoute = useCallback(() => {
    setRoute(getRouteByPath(getCurrentPathname()));
  }, []);

  useEffect(() => {
    window.addEventListener("popstate", updateRoute);
    updateRoute();
    return () => window.removeEventListener("popstate", updateRoute);
  }, [updateRoute]);

  return route;
}
